import { isFunction } from '@damilaredev/utils'
import { StorageSerializers, useStorage } from '@vueuse/core'
import type {
  Company,
  PaymentKeyTypes,
  ResponseInterface,
  Session,
  User,
} from '~/types'
import { queryClient } from '~/plugins/vue-query'
import { CompanyTypes } from '~/enums'
import { isAuthenticationError } from '~/utils/auth'

export type AuthState = {
  loggedIn: boolean
  token: string
  user: User
}

export const useAuthStore = defineStore('auth', () => {
  const loggedIn = ref<boolean>(false)
  const token = ref<string | null>(null)
  const tokenName = ref('bunce-auth')
  const tokenCookie = useCookie<string | null>('bunce:token')

  const user = ref<User | null>({} as User)

  const sessionStorage = useStorage(tokenName.value, null, undefined, {
    serializer: StorageSerializers.object,
    listenToStorageChanges: false,
    writeDefaults: true,
  })

  const isEmailVerified = computed<boolean | undefined>(
    () => user.value?.email_verified_at,
  )

  const company = computed<Company | null>(() => user?.value?.company as Company)

  const isSubscriptionCompany = computed<boolean>(
    () => company.value?.company_type === CompanyTypes.SUBSCRIPTION,
  )

  const isFintechCompany = computed<boolean>(
    () => company.value?.company_type === CompanyTypes.FINTECH,
  )

  const hasPaymentKeys = computed(() =>
    Object.values<boolean>(
      (company.value?.payment_keys as unknown as ArrayLike<boolean>) || [],
    )?.some((value) => value === true),
  )

  const hasEmailKeys = computed(() =>
    Object.values<boolean>(company.value?.email_keys || [])?.some(
      (value) => value === true,
    ),
  )

  const hasAccountingKeys = computed(() =>
    Object.values<boolean>(company.value?.accounting_keys || [])?.some(
      (value) => value === true,
    ),
  )

  const hasSmsKeys = computed(() =>
    Object.values<boolean>(
      (company.value?.sms_keys as unknown as ArrayLike<boolean>) || [],
    )?.some((value) => value === true),
  )

  const hasActivePlan = computed(() => company.value?.has_active_plan)
  const hasTrialPlan = computed(
    () => company.value?.has_a_plan || user.value?.company?.has_a_plan,
  )

  const isSubscriptionDeactivated = computed(() => {
    const subscriptionStatus = company.value?.bunce_subscription?.status
    return (
      subscriptionStatus === 'deactivated' || subscriptionStatus === 'inactive'
    )
  })

  const getPaymentKeys = computed(
    () => company.value?.payment_keys as unknown as Readonly<PaymentKeyTypes>,
  )

  const getAccountingKeys = computed(
    () => company.value?.accounting_keys as unknown as Readonly<'has_zoho_key'>,
  )

  function setUser({
    user: userData,
    token: authToken,
  }: Pick<AuthState, 'token' | 'user'>) {
    user.value = userData
    token.value = authToken
    tokenCookie.value = authToken
    loggedIn.value = true
  }

  function removeUser() {
    user.value = null
    token.value = null
    tokenCookie.value = null
    loggedIn.value = false
  }

  function login({ user, token }: Pick<AuthState, 'token' | 'user'>) {
    setUser({ user, token })
    saveUser()
  }

  function updateUser(session: Session) {
    user.value = {
      ...user.value,
      ...session,
    }
    saveUser()
  }

  function saveUser() {
    sessionStorage.value = {
      loggedIn: loggedIn.value,
      token: token.value,
      user: user.value,
    }
  }

  function loadUserSession() {
    if (!sessionStorage.value) return

    login({ ...sessionStorage.value })
  }

  function logout() {
    removeUser()

    sessionStorage.value = null
    localStorage.removeItem(tokenName.value)
    queryClient?.clear()
  }

  async function getProfile({ onSuccess }: { onSuccess?: any }) {
    await useRequest()
      .get<ResponseInterface<Session>>('session', {
        baseURL: useRuntimeConfig().public.baseUrl,
      })
      .then((data) => {
        updateUser(data.data.data)
        if (isFunction(onSuccess)) {
          onSuccess()
        }
      })
      .catch((error) => {
        if (isAuthenticationError(error)) {
          logout()
        }
      })
  }

  return {
    loggedIn,
    user,
    token,
    login,
    loadUserSession,
    updateUser,
    logout,
    isEmailVerified,
    company,
    getProfile,
    hasEmailKeys,
    hasPaymentKeys,
    hasSmsKeys,
    hasAccountingKeys,
    isSubscriptionCompany,
    isFintechCompany,
    hasActivePlan,
    hasTrialPlan,
    isSubscriptionDeactivated,
    getPaymentKeys,
    getAccountingKeys,
  }
})
